/**
 * Pricing validation — after a run, compare the QuoteSnapshot taken before
 * 開始翻譯 with the *actual* provider usage of the job (translate/usage.ts
 * JobUsage, the authoritative accounting: terminology + translation + QA,
 * every retry included). No second token accounting is kept here.
 *
 * Actual cost rules:
 *   cached input   billed at the cached price; nonCachedInput = input − cached
 *   reasoning      already inside output, never added again
 *   actualVariableCost = actualApiCostTwd + estimatedPaymentFee(suggestedPrice)
 *   (the safety factor is quote protection only and is NOT a real cost)
 *
 * Everything here is Developer Mode material; User Mode never sees it.
 */

import type { JobUsageSnapshot } from '../translate/usage';
import { DEFAULT_PRICING_SETTINGS, type PricingSettings } from './config';
import { apiCostUsd, paymentFee, roundMoney, usdToTwd, type QuoteSnapshot } from './quote';

export type PricingSafetyResult = 'SAFE' | 'WARNING' | 'LOSS';

export interface ActualUsageSummary {
  actualInputTokens: number;
  actualCachedInputTokens: number;
  actualOutputTokens: number;
  actualReasoningTokens: number;
  /** input + output (JobUsage.totalProcessedTokens). */
  actualProcessedTokens: number;
  /** False when a stage ran without the provider reporting usage (totals under-count). */
  usageComplete: boolean;
}

export interface ActualCost extends ActualUsageSummary {
  actualNonCachedInputTokens: number;
  actualApiCostUsd: number;
  actualApiCostTwd: number;
}

/** What the run really sent, for the billable-vs-sent comparison (Developer Mode). */
export interface RunDelivery {
  /** Units handed to translateBlocks(). */
  unitsTotal: number;
  /** Units the gate refused at run time (must equal quote.excludedUnits). */
  skippedUnits: number;
  /** Units served from the translation cache (no provider call). */
  cachedUnits: number;
  translatedUnits: number;
  failedUnits: number;
}

export interface PricingValidation {
  pricingVersion: string;
  quoteId: string;
  documentFingerprint: string;
  scopeFingerprint: string;
  scopeMode: QuoteSnapshot['scopeMode'];

  // before
  billableChars: number;
  billableUnits: number;
  estimatedInputTokens: number;
  estimatedOutputTokens: number;
  estimatedProcessedTokens: number;
  estimatedApiCostTwd: number;
  safeEstimatedApiCostTwd: number;
  tierPrice: number;
  minimumSustainablePrice: number;
  suggestedPrice: number;

  // after
  actual: ActualCost;
  estimatedPaymentFee: number;
  actualVariableCost: number;
  grossProfit: number;
  /** grossProfit / suggestedPrice; 0 for a free run. */
  grossMarginRate: number;
  /** (actual − estimated) / estimated; positive = the quote under-estimated. NaN when nothing was estimated. */
  costEstimateErrorRate: number;
  tokenEstimateErrorRate: number;
  safetyResult: PricingSafetyResult;

  /** Quote billable units vs the run's provider-bound units. */
  delivery: RunDelivery | null;
  /** billableUnits − (unitsTotal − skippedUnits); 0 when quote and run agree. */
  billableUnitsDelta: number | null;
}

export function summarizeActualUsage(usage: JobUsageSnapshot): ActualUsageSummary {
  return {
    actualInputTokens: usage.totalInputTokens,
    actualCachedInputTokens: usage.totalCachedInputTokens,
    actualOutputTokens: usage.totalOutputTokens,
    actualReasoningTokens: usage.totalReasoningTokens,
    actualProcessedTokens: usage.totalProcessedTokens,
    usageComplete: usage.complete,
  };
}

/** Actual API cost from actual usage (cached split, reasoning not double counted). */
export function actualApiCost(usage: JobUsageSnapshot, settings: PricingSettings = DEFAULT_PRICING_SETTINGS): ActualCost {
  const summary = summarizeActualUsage(usage);
  const cached = Math.min(summary.actualCachedInputTokens, summary.actualInputTokens);
  const usd = apiCostUsd({ inputTokens: summary.actualInputTokens, cachedInputTokens: cached, outputTokens: summary.actualOutputTokens }, settings.api);
  return {
    ...summary,
    actualNonCachedInputTokens: summary.actualInputTokens - cached,
    actualApiCostUsd: Math.round(usd * 10_000) / 10_000,
    actualApiCostTwd: roundMoney(usdToTwd(usd, settings.api)),
  };
}

/** Relative error (actual − estimated) / estimated; NaN when nothing was estimated. */
export function estimateErrorRate(estimated: number, actual: number): number {
  if (!(estimated > 0)) return Number.NaN;
  return (actual - estimated) / estimated;
}

export function classifySafety(grossProfit: number, grossMarginRate: number, settings: PricingSettings = DEFAULT_PRICING_SETTINGS): PricingSafetyResult {
  if (grossProfit < 0) return 'LOSS';
  if (grossMarginRate < settings.safetyRules.warningBelowMargin) return 'WARNING';
  return 'SAFE';
}

/** Compare a quote with the job's actual usage. Pure. */
export function validateQuote(
  quote: QuoteSnapshot,
  usage: JobUsageSnapshot,
  delivery: RunDelivery | null = null,
  settings: PricingSettings = DEFAULT_PRICING_SETTINGS,
): PricingValidation {
  const actual = actualApiCost(usage, settings);
  // Payment fee for the price that will be charged; PAYUNi is not wired, so the reserve stands in.
  const fee = paymentFee(quote.suggestedPrice, settings);
  const variableCost = roundMoney(actual.actualApiCostTwd + fee);
  const grossProfit = roundMoney(quote.suggestedPrice - variableCost);
  const grossMarginRate = quote.suggestedPrice > 0 ? grossProfit / quote.suggestedPrice : 0;
  const sent = delivery ? delivery.unitsTotal - delivery.skippedUnits : null;
  return {
    pricingVersion: quote.pricingVersion,
    quoteId: quote.quoteId,
    documentFingerprint: quote.documentFingerprint,
    scopeFingerprint: quote.scopeFingerprint,
    scopeMode: quote.scopeMode,
    billableChars: quote.billableChars,
    billableUnits: quote.billableUnits,
    estimatedInputTokens: quote.estimatedInputTokens,
    estimatedOutputTokens: quote.estimatedOutputTokens,
    estimatedProcessedTokens: quote.estimatedProcessedTokens,
    estimatedApiCostTwd: quote.estimatedApiCostTwd,
    safeEstimatedApiCostTwd: quote.safeEstimatedApiCostTwd,
    tierPrice: quote.tierPrice,
    minimumSustainablePrice: quote.minimumSustainablePrice,
    suggestedPrice: quote.suggestedPrice,
    actual,
    estimatedPaymentFee: fee,
    actualVariableCost: variableCost,
    grossProfit,
    grossMarginRate,
    costEstimateErrorRate: estimateErrorRate(quote.estimatedApiCostTwd, actual.actualApiCostTwd),
    tokenEstimateErrorRate: estimateErrorRate(quote.estimatedProcessedTokens, actual.actualProcessedTokens),
    safetyResult: classifySafety(grossProfit, grossMarginRate, settings),
    delivery,
    billableUnitsDelta: sent === null ? null : quote.billableUnits - sent,
  };
}
